import {
  DATE_TIME_FILTER_FROM_FIELD_NAME,
  DATE_TIME_FILTER_TO_FIELD_NAME,
  SELECT_FILTER_ID_FIELD_NAME,
} from '@common/constants';
import { convertISOStringToLocalDateString } from '@gcl/react-core';
import React, { useEffect, useState } from 'react';
import { useQueryParams } from 'use-query-params';
import {
  ESelectKeyFieldType,
  IFilter,
  IFilterFieldConfig,
  IOptionFilter,
  SearchFilterType,
} from './searchFilter.models';
import {
  getActualFilterFieldName,
  getDateTimeFilterFieldNameFrom,
  getDateTimeFilterFieldNameTo,
  getFilterFieldNameIsFromDate,
  getSelectFilterFieldNameId,
} from './searchFilter.services';

const SelectWithSearchParam = {
  encode: (selected?: IOptionFilter[] | IOptionFilter, allowMultipleSelect = false) => {
    if (allowMultipleSelect) {
      const jsonStrArray = ((selected ?? []) as IOptionFilter[]).map((s) => {
        return `id-${s.id}_value-${s.value}`;
      });
      return jsonStrArray;
    }
    if (!selected) return undefined;
    const s = selected as IOptionFilter;
    return `id-${s.id}_value-${s.value}`;
  },

  decode: (
    value: any,
    allowMultipleSelect = false,
    selectKeyFieldType: ESelectKeyFieldType = ESelectKeyFieldType.number,
  ) => {
    const decodeJson = (s: string) => {
      let json: { [key: string | number]: any } = {};
      s.split('_').map((kvp) => {
        const [k, v] = kvp.split('-');
        json = {
          ...json,
          [k]:
            k === SELECT_FILTER_ID_FIELD_NAME && selectKeyFieldType === ESelectKeyFieldType.number
              ? parseInt(v, 10)
              : v,
        };
      });
      return json;
    };
    if (!allowMultipleSelect) return decodeJson(value);
    if (typeof value !== 'object') return undefined;

    const strArray = value as string[];
    const arr = strArray.map((s) => decodeJson(s));
    return arr;
  },
};

export const useSearchFilterUrlParam = (
  queryParamsConfig: any,
  config: IFilterFieldConfig[],
  filterDefault?: IFilter,
) => {
  const [queryParams, setQueryParams] = useQueryParams(queryParamsConfig);
  const [localFilter, setLocalFilter] = useState<IFilter>(filterDefault ?? {});

  useEffect(() => {
    let newLocalFilter: IFilter = {};
    Object.keys(queryParams).map((queryParamKey) => {
      const queryParamValue = queryParams[queryParamKey];
      if (queryParamValue === undefined) return;
      const actualFieldName = getActualFilterFieldName(queryParamKey);

      const paramConfig = config.find((c) => c.fieldName === actualFieldName);
      if (!paramConfig) return;

      if (paramConfig.type === SearchFilterType.DateTime) {
        const isFromDate = getFilterFieldNameIsFromDate(queryParamKey);
        const dateTimeFilterValue = newLocalFilter[actualFieldName] ?? {};
        newLocalFilter = {
          ...newLocalFilter,
          [actualFieldName]: {
            ...dateTimeFilterValue,
            [isFromDate ? DATE_TIME_FILTER_FROM_FIELD_NAME : DATE_TIME_FILTER_TO_FIELD_NAME]: queryParamValue,
          },
        };
        return;
      }

      if (paramConfig.type === SearchFilterType.Select) {
        newLocalFilter = {
          ...newLocalFilter,
          [actualFieldName]: paramConfig.allowMultipleSelect
            ? (queryParamValue ?? []).map((v: any) => {
                return { [SELECT_FILTER_ID_FIELD_NAME]: v };
              })
            : { [SELECT_FILTER_ID_FIELD_NAME]: queryParamValue },
        };
        return;
      }
      if (
        paramConfig.type === SearchFilterType.SelectWithSearch ||
        paramConfig.type === SearchFilterType.CheckboxGroup
      ) {
        const filterValue = SelectWithSearchParam.decode(
          queryParamValue,
          paramConfig.type === SearchFilterType.CheckboxGroup ? true : paramConfig.allowMultipleSelect,
          paramConfig.selectKeyFieldType,
        );
        newLocalFilter = {
          ...newLocalFilter,
          [actualFieldName]: filterValue,
        };

        return;
      }
      newLocalFilter = {
        ...newLocalFilter,
        [queryParamKey]: paramConfig.shouldCastNumber ? castToNumber(queryParamValue) : queryParamValue,
      };
    });
    setLocalFilter(newLocalFilter);
  }, [JSON.stringify(queryParams)]);

  React.useEffect(() => {
    let newQueryParams = {};
    Object.keys(localFilter).map((localFilterKey) => {
      const localFilterValue = localFilter[localFilterKey];
      const paramConfig = config.find((c) => c.fieldName === localFilterKey);
      if (!paramConfig) return;

      if (paramConfig.type === SearchFilterType.DateTime) {
        newQueryParams = {
          ...newQueryParams,
          [getDateTimeFilterFieldNameFrom(localFilterKey)]: localFilterValue.from
            ? convertISOStringToLocalDateString(localFilterValue.from)
            : undefined,
          [getDateTimeFilterFieldNameTo(localFilterKey)]: localFilterValue.to
            ? convertISOStringToLocalDateString(localFilterValue.to)
            : undefined,
        };
        return;
      }
      if (paramConfig.type === SearchFilterType.Select) {
        newQueryParams = {
          ...newQueryParams,
          [getSelectFilterFieldNameId(localFilterKey)]: paramConfig.allowMultipleSelect
            ? (localFilterValue ?? []).map((v: any) => v.id)
            : localFilterValue?.id,
        };
        return;
      }
      if (
        paramConfig.type === SearchFilterType.SelectWithSearch ||
        paramConfig.type === SearchFilterType.CheckboxGroup
      ) {
        newQueryParams = {
          ...newQueryParams,
          [localFilterKey]: SelectWithSearchParam.encode(
            localFilterValue,
            paramConfig.type === SearchFilterType.CheckboxGroup ? true : paramConfig.allowMultipleSelect,
          ),
        };
        return;
      }
      newQueryParams = { ...newQueryParams, [localFilterKey]: localFilterValue };
    });
    setQueryParams(newQueryParams);
  }, [localFilter]);

  return { localFilter, setLocalFilter };
};

const castToNumber = (value: any) => {
  if (typeof value === 'function') return undefined;
  if (Array.isArray(value)) {
    const arr = value as Array<any>;
    return arr.map((v) => Number(v));
  }
  return Number(value);
};
